import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, ArrowLeft, AlertTriangle, CheckCircle } from 'lucide-react';

export default function DeleteAccountPage() {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm your request.');
      return;
    }

    // In a real implementation, this would call an API endpoint
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-2">
                <Home className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-blue-900">Move-it</span>
              </Link>
            </div>
          </div>
        </header>

        {/* Success Message */}
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Request Submitted</h1>
            <p className="text-gray-600 mb-6">
              We have received your account deletion request. Our team will process your request
              within 30 days and send a confirmation to <strong>{email}</strong>.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              If you have any questions, please contact us at privacy@move-it.com
            </p>
            <Link
              to="/"
              className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <Home className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-blue-900">Move-it</span>
            </Link>
            <Link
              to="/"
              className="flex items-center text-blue-600 font-medium hover:text-blue-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-blue-900 mb-4">Delete Your Account</h1>
        <p className="text-gray-600 mb-8">
          Use this form to request deletion of your Move-it account and all associated data.
        </p>

        {/* Warning Box */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <AlertTriangle className="w-6 h-6 text-red-500 mr-4 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold text-red-800 mb-2">Important Information</h2>
              <ul className="text-red-700 text-sm space-y-2">
                <li>Account deletion is permanent and cannot be undone.</li>
                <li>All your personal data, property listings, and transaction history will be deleted.</li>
                <li>Any active transactions must be completed or cancelled before deletion.</li>
                <li>This process may take up to 30 days to complete.</li>
                <li>You will receive a confirmation email once deletion is complete.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
          <div className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter the email associated with your account"
                required
              />
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Deletion (Optional)
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Help us improve by sharing why you're leaving..."
              />
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-2">
                Type DELETE to confirm <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="DELETE"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              Submit Deletion Request
            </button>
          </div>
        </form>

        <p className="text-gray-500 text-sm text-center mt-6">
          For questions about data deletion, contact{' '}
          <a href="mailto:privacy@move-it.com" className="text-blue-600 hover:underline">
            privacy@move-it.com
          </a>
        </p>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="flex items-center space-x-2">
              <Home className="w-6 h-6 text-blue-400" />
              <span className="text-xl font-bold text-white">Move-it</span>
            </Link>
            <div className="flex space-x-6">
              <a href="#" className="hover:text-white">About</a>
              <a href="#" className="hover:text-white">Contact</a>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <a href="#" className="hover:text-white">Terms</a>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p className="mb-2">Move-it is a marketplace facilitating connections. We do not represent any party.</p>
            <p>&copy; 2024 Move-it. All rights reserved. Licensed to operate in Texas.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
